
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Copy, Link, FileText, ExternalLink } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useOnboardingPhases } from "./hooks/useOnboardingPhases";
import { OnboardingForm } from "@/types/onboarding";

interface OnboardingLinkManagerProps {
  form: OnboardingForm;
  onLinkGenerated?: () => void;
}

interface OnboardingLink {
  id: string;
  form_id: string;
  unique_link: string;
  status: string;
  created_at: string;
}

export const OnboardingLinkManager = ({ form, onLinkGenerated }: OnboardingLinkManagerProps) => {
  const [links, setLinks] = useState<OnboardingLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { generateOnboardingLink, isUpdating } = useOnboardingPhases();

  useEffect(() => {
    if (form?.id) {
      fetchLinks();
    }
  }, [form?.id]);

  const fetchLinks = async () => {
    if (!form?.id) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('onboarding_links')
        .select('*')
        .eq('form_id', form.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setLinks(data || []);
    } catch (error) {
      console.error('Error fetching onboarding links:', error);
      toast({
        title: "Error",
        description: "Failed to load onboarding links",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    if (!form?.id) return;
    
    const newLink = await generateOnboardingLink(form.id);
    if (newLink) {
      toast({
        title: "Link Generated",
        description: "New onboarding link has been created"
      });
      fetchLinks();
      if (onLinkGenerated) onLinkGenerated();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast({
          title: "Copied",
          description: "Link copied to clipboard"
        });
      })
      .catch(() => {
        toast({
          title: "Failed to copy",
          description: "Could not copy to clipboard",
          variant: "destructive"
        });
      });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!form) return null;

  return (
    <Card className="shadow-sm border border-gray-200 dark:border-gray-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex justify-between items-center">
          <span>Onboarding Links</span>
          <Button 
            size="sm"
            onClick={handleGenerateLink}
            disabled={isUpdating}
            className="flex items-center gap-1"
          >
            <Link className="h-4 w-4" />
            Generate Link
          </Button>
        </CardTitle>
      </CardHeader>
      <Separator />
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">Loading links...</p>
          </div>
        ) : links.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">No links generated yet</p>
            <p className="text-xs text-gray-400 mt-1">Click the button above to generate a new link</p>
          </div>
        ) : (
          <div className="space-y-3">
            {links.map((link) => (
              <div key={link.id} className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 text-sm">
                <div className="flex justify-between items-start">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">{form.kol_name}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Generated: {formatDate(link.created_at)}
                    </div>
                  </div>
                  <Badge 
                    variant={link.status === 'active' ? 'default' : 'secondary'}
                    className="capitalize"
                  >
                    {link.status}
                  </Badge>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 truncate bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-xs">
                    {link.unique_link}
                  </div>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    onClick={() => copyToClipboard(link.unique_link)}
                    className="h-7 w-7"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    onClick={() => window.open(link.unique_link, '_blank')}
                    className="h-7 w-7"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
